"use client";

import { useEffect } from "react";
import HeroSection from "./components/CoralGablesHero";
import NeighborhoodStats from "@/components/feature/NeighborhoodStats";
import RepairServicesGrid from "@/components/feature/RepairServicesGrid";
import ScopeDisclaimer from "@/components/feature/ScopeDisclaimer";
import NeighborhoodCta from "@/components/feature/NeighborhoodCta";
import QuartierCrossLinks from "@/components/feature/QuartierCrossLinks";
import { injectMetaTags } from "@/lib/seoHelpers";
import FaqSection from "@/components/feature/FaqSection";
import RelatedServicesBlock from "@/components/feature/RelatedServicesBlock";
import NearbyAreasBlock from "@/components/feature/NearbyAreasBlock";
import AboutAreaSection from "@/components/feature/AboutAreaSection";
import { useLanguage } from "@/i18n/LanguageContext";
import { useHreflang } from "@/i18n/useHreflang";

const neighborhood = "Coral Gables";
const zips = ["33134", "33146", "33156"];

export default function CoralGablesPage() {
  const { t, currentLang } = useLanguage();
  useHreflang();

  const coralGablesFaqs = [
    {
      question: t("hood.faq1Q"),
      answer: t("hood.faq1A"),
    },
    {
      question: t("hood.faq2Q"),
      answer: t("hood.faq2A"),
    },
    {
      question: t("hood.faq3Q"),
      answer: t("hood.faq3A"),
    },
    {
      question: t("hood.faq4Q"),
      answer: t("hood.faq4A"),
    },
    {
      question: t("coralGables.faq5Q"),
      answer: t("coralGables.faq5A"),
    },
  ];

  useEffect(() => {
    document.title = currentLang === "es"
      ? "Reparación de Ventanas Coral Gables — Casas Históricas"
      : "Coral Gables Window Repair | Historic Home Specialists";

    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = currentLang === "es"
      ? "Especialistas en reparación de ventanas en Coral Gables. Experiencia en casas históricas 1920s-1940s. Servicio de marcos casement y double-hung vintage preservando el carácter arquitectónico."
      : "Coral Gables window repair specialists. Historic 1920s-1940s home expertise. Vintage casement and double-hung frame service preserving architectural character.";

    const cleanupSeo = injectMetaTags({
      title: currentLang === "es"
        ? "Reparación de Ventanas Coral Gables — Casas Históricas"
        : "Coral Gables Window Repair | Historic Home Specialists",
      description: currentLang === "es"
        ? "Especialistas en reparación de ventanas en Coral Gables. Experiencia en casas históricas 1920s-1940s. Servicio de marcos casement y double-hung vintage preservando el carácter arquitectónico."
        : "Coral Gables window repair specialists. Historic 1920s-1940s home expertise. Vintage casement and double-hung frame service preserving architectural character.",
      url: "https://impact-window-repair-miami.com/impact-windows-coral-gables/",
      image: "https://impact-window-repair-miami.com/og/og-coral-gables.jpg",
      imageAlt: currentLang === "es"
        ? "Reparación de ventanas Coral Gables Miami"
        : "Window repair Coral Gables Miami",
      breadcrumbs: [
        { name: "Home", item: "https://impact-window-repair-miami.com/" },
        { name: "Service Areas", item: "https://impact-window-repair-miami.com/service-areas/" },
        { name: "Coral Gables", item: "https://impact-window-repair-miami.com/impact-windows-coral-gables/" }
      ],
      type: "website",
    });

    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          name: currentLang === "es" ? "Reparación de Ventanas" : "Window Repair",
          inLanguage: currentLang === "es" ? "es-US" : "en-US",
          provider: {
            "@type": "LocalBusiness",
            name: "Windows and Doors Repair LLC",
            telephone: "+17863774452",
            email: "contact@impact-window-repair-miami.com",
            address: {
              "@type": "PostalAddress",
              streetAddress: "848 Brickell Ave, PH-5",
              addressLocality: "Miami",
              addressRegion: "FL",
              postalCode: "33131",
              addressCountry: "US",
            },
            geo: {
              "@type": "GeoCoordinates",
              latitude: 25.7617,
              longitude: -80.1918,
            },
            openingHoursSpecification: [{
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
              opens: "07:00",
              closes: "21:00",
            }],
            priceRange: "$$",
            paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
            currenciesAccepted: "USD",
            areaServed: [
              { "@type": "City", name: "Miami" },
              { "@type": "AdministrativeArea", name: "Miami-Dade County" },
              "Brickell", "Aventura", "Miami Beach", "Coral Gables", "Coconut Grove",
              "Doral", "Kendall", "Pinecrest", "Bay Harbor Islands", "Key Biscayne",
              "South Miami", "North Miami Beach",
            ],
            knowsLanguage: ["en", "es"],
            knowsAbout: ["Window Hardware Repair", "Sliding Door Repair", "Window Glass Replacement", "Weatherstripping", "Window Maintenance", "Emergency Board-Up"],
            slogan: "Honest Miami Window Repair. Same-Day. 30-Day Guarantee.",
          },
          areaServed: { "@type": "City", name: "Coral Gables, Miami-Dade, FL" },
          serviceType: "Window Repair",
          description: currentLang === "es"
            ? "Reparación de herrajes, vidrios y mantenimiento en Coral Gables."
            : "Hardware repair, glass replacement, and maintenance in Coral Gables.",
        },
        {
          "@type": "LocalBusiness",
          inLanguage: currentLang === "es" ? "es-US" : "en-US",
          name: "Windows and Doors Repair LLC",
          telephone: "+17863774452",
          email: "contact@impact-window-repair-miami.com",
          address: {
            "@type": "PostalAddress",
            streetAddress: "848 Brickell Ave, PH-5",
            addressLocality: "Miami",
            addressRegion: "FL",
            postalCode: "33131",
            addressCountry: "US",
          },
          geo: {
            "@type": "GeoCoordinates",
            latitude: 25.7617,
            longitude: -80.1918,
          },
          openingHoursSpecification: [{
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            opens: "07:00",
            closes: "21:00",
          }],
          priceRange: "$$",
          paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
          currenciesAccepted: "USD",
          areaServed: [
            { "@type": "City", name: "Miami" },
            { "@type": "AdministrativeArea", name: "Miami-Dade County" },
            "Brickell", "Aventura", "Miami Beach", "Coral Gables", "Coconut Grove",
            "Doral", "Kendall", "Pinecrest", "Bay Harbor Islands", "Key Biscayne",
            "South Miami", "North Miami Beach",
          ],
          knowsLanguage: ["en", "es"],
          knowsAbout: ["Window Hardware Repair", "Sliding Door Repair", "Window Glass Replacement", "Weatherstripping", "Window Maintenance", "Emergency Board-Up"],
          slogan: "Honest Miami Window Repair. Same-Day. 30-Day Guarantee.",
          url: window.location.href,
        },
      ],
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "main-schema";
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      inLanguage: currentLang === "es" ? "es-US" : "en-US",
      mainEntity: coralGablesFaqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    };

    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.id = "faq-schema";
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    return () => {
      cleanupSeo();
      const s = document.getElementById("main-schema");
      if (s) document.head.removeChild(s);
      const fs = document.getElementById("faq-schema");
      if (fs) document.head.removeChild(fs);
    };
  }, [currentLang]);

  return (
    <main className="min-h-screen">
      <HeroSection h1={t("coralGables.h1")} subhead={t("coralGables.subhead")} zips={zips} />
      <AboutAreaSection
        badge={currentLang === "es" ? "REPARACIÓN DE VENTANAS CORAL GABLES" : "CORAL GABLES WINDOW REPAIR"}
        imageSrc="https://storage.readdy-site.link/project_files/76915537-ee3f-4564-9a23-41bd98b5df27/2f6d99ac-8848-4faa-aab4-18dac1881956_Coral-Gables-Historic-Home-Window-Repair.jpg?v=a525f23dc7112961da30ededfca2ed43"
        imageAlt={currentLang === "es"
          ? "Especialista en reparación de ventanas Coral Gables trabajando en casement vintage de 1940s"
          : "Coral Gables window repair specialist working on historic 1940s casement window"}
        heading={currentLang === "es"
          ? "Reparación de Ventanas en Casas Históricas Coral Gables"
          : "Coral Gables Historic Home Window Repair"}
        lead={t("coralGables.intro")}
        bullets={
          currentLang === "es"
            ? [
                "Reparación de herrajes en ventanas casement y double-hung vintage",
                "Reemplazo de vidrios no impacto en marcos originales",
                "Burletes para construcción de ventanas más antiguas",
              ]
            : [
                "Hardware repair on vintage casement and double-hung windows",
                "Non-impact glass replacement in original frames",
                "Weatherstripping for older window construction",
              ]
        }
        closing={currentLang === "es"
          ? "Nuestros técnicos trabajan cuidadosamente en propiedades históricas de Coral Gables para preservar la integridad arquitectónica."
          : "Our techs work carefully on Coral Gables historic properties to preserve architectural integrity."}
      />
      <NeighborhoodStats />
      <RepairServicesGrid heading={t("hood.commonH2")} />
      <ScopeDisclaimer neighborhood={neighborhood} />
      <FaqSection items={coralGablesFaqs} heading={t("hood.commonFaqH2")} />
      <RelatedServicesBlock neighborhood={neighborhood} />
      <NearbyAreasBlock neighborhood={neighborhood} neighbors={[
        { name: "Coconut Grove", slug: "coconut-grove" },
        { name: "South Miami", slug: "south-miami" },
        { name: "Brickell", slug: "brickell" },
      ]} />
      <NeighborhoodCta neighborhood={neighborhood} heading={t("hood.commonCta")} />
      <QuartierCrossLinks />
    </main>
  );
}